import React, { useState, useRef, useEffect } from 'react';
import './AudioPlayer.css';

const AudioPlayer = ({ 
  audioSrc = '', 
  title = 'Audio Player', 
  onPlay = () => {}, 
  onPause = () => {}, 
  onEnded = () => {},
  showDownload = true,
  showSpeedControl = true,
  showVolumeControl = true,
  initialVolume = 1.0,
  initialPlaybackRate = 1.0,
  className = ''
}) => {
  const normalizedAudioSrc = typeof audioSrc === 'string' ? audioSrc.trim() : '';
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(initialPlaybackRate);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [volume, setVolume] = useState(initialVolume);
  const [isMuted, setIsMuted] = useState(false);
  const [audioError, setAudioError] = useState(false);
  
  const audioRef = useRef(null);
  const progressRef = useRef(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      if (onEnded) onEnded();
    };

    const handleCanPlay = () => {
      setAudioError(false);
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('canplay', handleCanPlay);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('canplay', handleCanPlay);
    };
  }, [onEnded, normalizedAudioSrc]);

  const togglePlayPause = () => {
    if (isPlaying) {
      audioRef.current.pause();
      if (onPause) onPause();
    } else {
      audioRef.current.play();
      if (onPlay) onPlay();
    }
  };

  const handleProgressClick = (e) => {
    const rect = progressRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const progressWidth = rect.width;
    const clickPercent = clickX / progressWidth;
    const newTime = clickPercent * duration;
    audioRef.current.currentTime = newTime;
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    audioRef.current.volume = newVolume;
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    if (isMuted) {
      audioRef.current.volume = volume;
      setIsMuted(false);
    } else {
      audioRef.current.volume = 0;
      setIsMuted(true);
    }
  };

  const changePlaybackRate = (rate) => {
    setPlaybackRate(rate);
    audioRef.current.playbackRate = rate;
    setShowSpeedMenu(false);
  };

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleDownload = () => {
    if (normalizedAudioSrc) {
      const link = document.createElement('a');
      link.href = normalizedAudioSrc;
      link.download = title || 'audio-file.mp3';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    setShowDownloadMenu(false);
  };

  const handleAudioError = (e) => {
    setAudioError(true);
  };

  const speedOptions = [0.5, 0.75, 1.0, 1.25, 1.5, 2.0];

  if (!normalizedAudioSrc || audioError) {
    return null;
  }

  return (
    <div className={`audio-player ${className}`}>
      <>
        <audio 
          ref={audioRef} 
          src={normalizedAudioSrc}
          preload="metadata" 
          onError={handleAudioError}
        />
        
        {/* Progress Bar */}
        <div className="progress-container" ref={progressRef} onClick={handleProgressClick}>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${(currentTime / duration) * 100}%` }}
            />
          </div>
          <div className="time-display">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        {/* Controls */}
        <div className="audio-controls">
          {/* Play/Pause Button */}
          <button 
            className="control-btn play-btn" 
            onClick={togglePlayPause}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            <i className={`fas fa-${isPlaying ? 'pause' : 'play'}`} />
          </button>

          {/* Volume Control */}
          {showVolumeControl && (
            <div className="volume-control">
              <button 
                className="control-btn volume-btn" 
                onClick={toggleMute}
                aria-label={isMuted ? 'Unmute' : 'Mute'}
              >
                <i className={`fas fa-volume-${isMuted ? 'mute' : 'up'}`} />
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
                className="volume-slider"
                aria-label="Volume"
              />
            </div>
          )}

          {/* Playback Speed */}
          {showSpeedControl && (
            <div className="speed-control">
              <button 
                className="control-btn speed-btn" 
                onClick={() => setShowSpeedMenu(!showSpeedMenu)}
                aria-label="Playback Speed"
              >
                <i className="fas fa-cog" />
                <span className="speed-label">{playbackRate}x</span>
              </button>
              {showSpeedMenu && (
                <div className="speed-menu">
                  {speedOptions.map(speed => (
                    <button
                      key={speed}
                      className={`speed-option ${playbackRate === speed ? 'active' : ''}`}
                      onClick={() => changePlaybackRate(speed)}
                    >
                      {speed}x
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Download Menu */}
          {showDownload && (
            <div className="download-control">
              <button 
                className="control-btn download-btn" 
                onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                aria-label="Download Options"
              >
                <i className="fas fa-ellipsis-v" />
              </button>
              {showDownloadMenu && (
                <div className="download-menu">
                  <button className="download-option" onClick={handleDownload}>
                    <i className="fas fa-download" />
                    Download Audio
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </>
    </div>
  );
};

export default AudioPlayer;
